import { useCallback, useRef, useState } from 'react';

import type { SyncedBlockFeedbackContext } from '@atlaskit/editor-plugin-synced-block/synced-block-plugin-type';

import { getLogger } from '@confluence/logger';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const logger = getLogger('SyncedBlockFeedback');

type SyncedBlockFeedbackControls = {
	blockType: SyncedBlockFeedbackContext['blockType'];
	entryPoint: SyncedBlockFeedbackContext['entryPoint'];
	isOpen: boolean;
	onClose: () => void;
	onFeedbackPromptShown: (() => void) | undefined;
	onGiveFeedback: ((context: SyncedBlockFeedbackContext) => void) | undefined;
	shouldShowFeedbackPrompt: (() => boolean) | undefined;
};

export const useSyncedBlockFeedback = (): SyncedBlockFeedbackControls => {
	const [isOpen, setIsOpen] = useState(false);
	const [blockType, setBlockType] = useState<SyncedBlockFeedbackContext['blockType']>('reference');
	const [entryPoint, setEntryPoint] =
		useState<SyncedBlockFeedbackContext['entryPoint']>('overflow-menu');
	const promptedFeedbackConsumed = useRef(false);

	const onGiveFeedback = useCallback(
		({ blockType: nextBlockType, entryPoint: nextEntryPoint }: SyncedBlockFeedbackContext) => {
			setBlockType(nextBlockType);
			setEntryPoint(nextEntryPoint);
			setIsOpen(true);
		},
		[],
	);
	const shouldShowFeedbackPrompt = useCallback(() => {
		if (promptedFeedbackConsumed.current) {
			return false;
		}

		const storageKey =
			PERSISTED_KEYS_ON_SERVER.PERSISTED_SYNCED_BLOCK_DESTRUCTIVE_FEEDBACK_PROMPT_SHOWN;
		try {
			if (confluenceLocalStorageInstance.getItem(storageKey)) {
				promptedFeedbackConsumed.current = true;
				return false;
			}
		} catch (error) {
			promptedFeedbackConsumed.current = true;
			logger.warn`Unable to read the synced block feedback prompt allowance from local storage: ${error}`;
			return false;
		}
		return true;
	}, []);
	const onFeedbackPromptShown = useCallback(() => {
		if (promptedFeedbackConsumed.current) {
			return;
		}
		promptedFeedbackConsumed.current = true;

		const storageKey =
			PERSISTED_KEYS_ON_SERVER.PERSISTED_SYNCED_BLOCK_DESTRUCTIVE_FEEDBACK_PROMPT_SHOWN;
		try {
			confluenceLocalStorageInstance.setItem(storageKey, 'true');
		} catch (error) {
			logger.warn`Unable to persist the synced block feedback prompt allowance to local storage: ${error}`;
		}
	}, []);
	const onClose = useCallback(() => setIsOpen(false), []);

	return {
		blockType,
		entryPoint,
		isOpen,
		onClose,
		onFeedbackPromptShown,
		onGiveFeedback,
		shouldShowFeedbackPrompt,
	};
};
